import {
	ActionButton,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	Text,
} from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import More from '@react-spectrum/s2/icons/More';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { BlobImage } from '@/components/blob/BlobImage';
import { logger } from '@/logger';
import { dismissDeletedArtifact } from '@/spaces/content/contentDetailUtils';
import { DeleteConfirmationWrapper } from '@/spaces/content/DeleteConfirmationWrapper';
import {
	asReasonList,
	formatInspirationMeta,
	isUsableDesignBrief,
	noDesignReadMessage,
} from '@/spaces/content/inspirationFormatting';
import { useInspirationChat } from '@/spaces/content/useInspirationChat';
import { trackEvent } from '@/utils/fullstory';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';

const errorTextStyles = style({
	font: 'body',
	fontSize: 'body',
	color: 'body',
});

const sectionLabelStyles = style({
	font: 'title',
	fontSize: 'title-sm',
	fontWeight: 'bold',
	color: 'title',
});

const sectionBodyStyles = style({
	font: 'body',
	fontSize: 'body',
	color: 'body',
});

const metaStyles = style({
	font: 'body',
	fontSize: 'body-sm',
	color: 'body',
});

interface InspirationDetailProps {
	artifact: InspirationReference;
	refresh?: () => Promise<void>;
}

const AnalysisSection = ({
	label,
	body,
}: {
	label: string;
	body: string | undefined;
}) => {
	if (!body?.trim()) return null;
	return (
		<div className="flex flex-col gap-2">
			<Text styles={sectionLabelStyles}>{label}</Text>
			<Text styles={sectionBodyStyles}>
				<span className="whitespace-pre-wrap">{body}</span>
			</Text>
		</div>
	);
};

export const InspirationDetail = ({
	artifact,
	refresh,
}: InspirationDetailProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [mutationError, setMutationError] = useState<string | null>(null);

	const handleUseAsInspiration = useInspirationChat(artifact);
	// Without a design read there is nothing for a carousel to match, so the
	// action would open a chat that quietly builds a plain brand carousel. The
	// row stays in the library either way: it is still a saved post with a
	// caption analysis.
	//
	// `isUsableDesignBrief` mirrors the server predicate, so it also rejects the
	// boilerplate scaffold that `assemble_design` renders for an empty read. A
	// non-empty test would leave the action ENABLED on such a record, and the
	// server resolver would then refuse the same row, so the customer would get
	// a plain brand carousel with no explanation.
	const hasUsableDesign = isUsableDesignBrief(artifact.content?.design_read);

	const handleDelete = async () => {
		if (!userId) return;
		setIsDeleting(true);
		setMutationError(null);
		try {
			// Archiving is how every other artifact in this UI is "deleted": the row
			// drops out of the library the user sees, and nothing is destroyed.
			await bridgewayApi.patch(`/api/user/${userId}/artifacts/${artifact.id}`, {
				status: 'archived',
			});
			trackEvent('Inspiration Deleted', { artifact_id: artifact.id });
			setShowDeleteConfirmation(false);
			await refresh?.();
			dismissDeletedArtifact(bridgeApi, artifact.id);
		} catch (error) {
			setMutationError('Failed to delete inspiration. Please try again.');
			logger.error('Failed to delete inspiration reference', error);
		} finally {
			setIsDeleting(false);
		}
	};

	const { content } = artifact;
	const title = content.topic || 'Saved inspiration';
	const meta = formatInspirationMeta(content);

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex items-center justify-end gap-4">
				{mutationError && (
					<span className="flex items-center gap-2">
						<AlertTriangle />
						<Text styles={errorTextStyles}>{mutationError}</Text>
					</span>
				)}
				{!hasUsableDesign && (
					// Say why "Use as inspiration" is grey. A disabled item with no
					// reason next to it reads as a broken page, and the customer cannot
					// learn that the design read failed.
					//
					// The sentence grows into the space left of the ••• button and wraps
					// inside it, so a narrow window never pushes the button out of view.
					<p
						data-testid="inspiration-no-design"
						className="min-w-0 flex-1 text-xs text-(--text-muted)"
					>
						{noDesignReadMessage(content.platform)}
					</p>
				)}
				<MenuTrigger>
					<DeleteConfirmationWrapper
						onConfirm={handleDelete}
						onCancel={() => setShowDeleteConfirmation(false)}
						isOpen={showDeleteConfirmation}
						title="Delete inspiration?"
						body="This reference will be removed from your saved inspiration."
					>
						<ActionButton isQuiet aria-label="More options">
							<More />
						</ActionButton>
					</DeleteConfirmationWrapper>
					<Popover>
						<Menu>
							<MenuItem
								id="use-as-inspiration"
								onPress={handleUseAsInspiration}
								isDisabled={!hasUsableDesign}
							>
								<Text>Use as inspiration</Text>
							</MenuItem>
							<MenuItem
								id="delete"
								onPress={() => setShowDeleteConfirmation(true)}
								isDisabled={isDeleting}
							>
								<Text>Delete</Text>
							</MenuItem>
						</Menu>
					</Popover>
				</MenuTrigger>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
				<div className="flex flex-col gap-4 md:flex-row md:items-start">
					<BlobImage
						src={content.thumbnail_url}
						alt={title}
						aria-label={title}
						fallback={{}}
						className="aspect-square w-full max-w-64 shrink-0 rounded-lg
							object-cover"
					/>
					<div className="flex min-w-0 flex-col gap-2">
						<Text styles={metaStyles}>{meta}</Text>
						{content.source_url && (
							<a
								href={content.source_url}
								target="_blank"
								rel="noreferrer"
								className="w-fit text-sm text-(--text-secondary) underline
									underline-offset-4 hover:text-(--text-primary)"
							>
								View original post
							</a>
						)}
					</div>
				</div>

				<AnalysisSection label="Topic" body={content.topic} />
				<AnalysisSection
					label="Imagery analysis"
					body={content.imagery_analysis}
				/>
				<AnalysisSection
					label="Content analysis"
					body={content.content_analysis}
				/>
				<AnalysisSection label="Hook" body={content.hook} />
				<AnalysisSection label="Pacing" body={content.pacing} />
				<AnalysisSection label="Payoff" body={content.payoff} />
				<AnalysisSection label="What to borrow" body={content.suggestion} />
				{/* Appended BELOW the existing labels on purpose: the ADO-1874
				    Content-space spec asserts the order above, and a record saved
				    before the lesson existed renders none of these three. */}
				<AnalysisSection label="The lesson" body={content.learning} />
				<AnalysisSection
					label="Why it works"
					body={asReasonList(content.why_it_works)
						.filter((point) => point.trim())
						.map((point) => `• ${point.trim()}`)
						.join('\n')}
				/>
				<AnalysisSection label="Repeatable format" body={content.format_name} />
				<AnalysisSection label="Copy" body={content.content_copy} />
			</div>
		</div>
	);
};
